import  { useState, useEffect } from 'react';
import { Search, Phone, MapPin, Clock, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mainAdminApi } from '../../../config';
import labtestlogo from "../../../assets/Health care.png";
import { SEO } from '../../SEO';


interface LabTest {
  _id: string;
  name: string;
  category: string;
  price: number;
  originalPrice: number;
  parameters: string[];
  sampleType: string;
  fastingRequired: boolean;
  testId?: string;
  rating: number;
  reviews: number;
  discount: string;
}

const LabTestPage = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [labTests, setLabTests] = useState<LabTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>(['All']);

  // Fetch lab tests on component mount
  useEffect(() => {
    fetchLabTests();
  }, []);

  const fetchLabTests = async () => {
    try {
      setLoading(true);
      const response = await mainAdminApi.get<{ labTests: LabTest[] }>('/view-lab-tests');
      const tests = (response.data as any)?.labTests || [];
      setLabTests(tests);

      // Extract unique categories
      const uniqueCategories = new Set<string>(['All']);
      tests.forEach((test: LabTest) => {
        if (test.category) {
          uniqueCategories.add(test.category);
        }
      });
      setCategories(Array.from(uniqueCategories));
    } catch (error) {
      console.error('Failed to fetch lab tests:', error);
      setLabTests([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredTests = labTests.filter(test => {
    const categoryMatch = selectedCategory === 'All' || test.category === selectedCategory;
    const searchMatch = test.name.toLowerCase().includes(searchTerm.toLowerCase());
    return categoryMatch && searchMatch;
  });

  const faqs = [
    {
      question: "मैं ऑनलाइन लैब टेस्ट कैसे बुक करूँ?",
      answer: "आप हमारी वेबसाइट के माध्यम से या हमारी हेल्पलाइन को कॉल करके आसानी से लैब टेस्ट बुक कर सकते हैं। बस अपना टेस्ट चुनें, समय स्लॉट चुनें, और हमारी टीम आपके घर से सैंपल एकत्र करेगी।"
    },
    {
      question: "क्या सभी टेस्ट के लिए होम सैंपल कलेक्शन उपलब्ध है?",
      answer: "हाँ, हम अधिकांश लैब टेस्ट के लिए होम सैंपल कलेक्शन सेवाएँ प्रदान करते हैं। हमारे प्रशिक्षित फ़्लेबोटोमिस्ट आपके सुविधाजनक समय पर आपके घर जाएंगे।"
    },
    {
      question: "ऑनलाइन लैब टेस्ट बुक करने के लिए मुझे कौन सी जानकारी देनी होगी?",
      answer: "आपको नाम, संपर्क नंबर, पता और सैंपल कलेक्शन के लिए पसंदीदा समय स्लॉट जैसी बुनियादी जानकारी प्रदान करनी होगी।"
    },
    {
      question: "लैब टेस्ट के परिणाम पाने में कितना समय लगता है?",
      answer: "रिपोर्ट डिलीवरी का समय टेस्ट के प्रकार के अनुसार अलग-अलग होता है। अधिकांश नियमित टेस्ट 6-24 घंटों में उपलब्ध होते हैं, जबकि विशेष टेस्ट में अधिक समय लग सकता है।"
    },
    {
      question: "अगर मेरे लैब परिणामों के बारे में कोई सवाल हो तो क्या करूँ?",
      answer: "हमारे चिकित्सा विशेषज्ञ आपकी रिपोर्ट समझाने के लिए उपलब्ध हैं। आप विस्तृत व्याख्या के लिए हमारे डॉक्टरों से परामर्श भी ले सकते हैं।"
    },
    {
      question: "क्या लैब टेस्ट रिपोर्ट ऑनलाइन उपलब्ध हैं?",
      answer: "हाँ, सभी रिपोर्ट आपके खाते के माध्यम से ऑनलाइन उपलब्ध हैं। आप उन्हें ईमेल के माध्यम से भी प्राप्त करेंगे और कभी भी डाउनलोड कर सकते हैं।"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Book Diagnostic Lab Tests & Health Checkup Packages | MetaFit Wellness"
        description="Book diagnostic blood tests, preventive health packages, and home sample collection services with verified certified labs at MetaFit Wellness."
        canonical="/lab-test"
        keywords="lab tests, blood tests, health checkup, home sample collection, diagnostic lab, MetaFit Wellness"
        schema={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map((f) => ({
            "@type": "Question",
            "name": f.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": f.answer
            }
          }))
        }}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-teal-50 to-blue-50 py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 items-center">
            {/* Logo Section - Left */}
            <div className="flex justify-center md:justify-start">
              <div className="bg-white rounded-xl p-8 shadow-sm">
                <img src={labtestlogo} alt="Udaypur Healthcare Service Logo" className="h-56 md:h-64 w-auto" />
              </div>
            </div>

            {/* Services Section - Center */}
            <div className="bg-white rounded-xl shadow-md p-8 border border-gray-100">
              <p className="text-gray-700 mb-6 leading-relaxed text-base font-medium">
                आपको घर बैठे सुरक्षित, तेज और विश्वसनीय मेडिकल सुविधाएँ प्रदान करता है।
              </p>
              
              <h3 className="font-bold text-gray-900 mb-4 text-lg">हमारी सेवाओं में शामिल हैं:</h3>
              <div className="space-y-3 text-gray-700">
                <div className="flex items-start gap-3">
                  <span className="text-teal-600 font-bold text-xl flex-shrink-0">✔</span>
                  <span className="text-sm">घर पर लैब टेस्ट की सुविधा</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-teal-600 font-bold text-xl flex-shrink-0">✔</span>
                  <span className="text-sm">घर पर ECG टेस्ट</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-teal-600 font-bold text-xl flex-shrink-0">✔</span>
                  <span className="text-sm">अनुभवी डॉक्टरों से परामर्श (ऑनलाइन एवं विज़िट)</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-teal-600 font-bold text-xl flex-shrink-0">✔</span>
                  <span className="text-sm">नियमित हेल्थ चेकअप पैकेज</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-teal-600 font-bold text-xl flex-shrink-0">✔</span>
                  <span className="text-sm">सैंपल कलेक्शन की होम सर्विस</span>
                </div>
              </div>
              
              <p className="text-gray-600 italic mt-6 pt-6 border-t border-gray-200 text-xs leading-relaxed">
                हमारा उद्देश्य है – "हर परिवार तक बेहतर स्वास्थ्य सेवाएँ पहुँचाना, सरल और किफायती तरीके से।"
              </p>
            </div>

            {/* Report Timeline Notice and Fasting Guidelines - Right */}
            <div className="space-y-4">
              {/* Report Timeline Notice */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-l-4 border-blue-600 rounded-xl p-8 shadow-md">
                <div className="flex items-start gap-3 mb-4">
                  <span className="text-blue-600 text-2xl">⏱️</span>
                  <p className="font-bold text-blue-900 text-lg">रिपोर्ट डिलीवरी समयरेखा</p>
                </div>
                <p className="text-blue-800 text-base leading-relaxed">
                  रिपोर्ट सैंपल संग्रह के <span className="font-semibold">24-48 घंटे</span> के भीतर प्रदान की जाएगी। आप ईमेल और व्हाट्सएप के माध्यम से अपनी रिपोर्ट प्राप्त करेंगे।
                </p>
              </div>

              {/* Fasting Guidelines Notice */}
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 border-l-4 border-amber-600 rounded-xl p-8 shadow-md">
                <div className="flex items-start gap-3 mb-4">
                  <span className="text-amber-600 text-2xl">🚫</span>
                  <p className="font-bold text-amber-900 text-lg">उपवास (फास्टिंग) आवश्यकताएं</p>
                </div>
                <p className="text-amber-800 text-base leading-relaxed">
                  यदि किसी टेस्ट के लिए उपवास आवश्यक है, तो कृपया सैंपल संग्रह से पहले कम से कम <span className="font-semibold">10-12 घंटे</span> का उपवास करें। सटीक परिणामों के लिए यह बहुत महत्वपूर्ण है।
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="bg-white py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="लैब टेस्ट खोजें..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lab Tests Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">हमारे लैब टेस्ट पैकेज</h2>
            <p className="text-gray-600">हमारी व्यापक निदान परीक्षाओं की रेंज से चुनें</p>
          </div>
           */}

          {/* Report Timeline Notice */}
          {/* <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded mb-8">
            <div className="flex items-start gap-3">
              <span className="text-blue-600 font-bold text-lg">ℹ️</span>
              <div>
                <p className="font-semibold text-blue-900 mb-1">रिपोर्ट डिलीवरी समयरेखा</p>
                <p className="text-blue-800 text-sm">
                  रिपोर्ट सैंपल संग्रह के 24-48 घंटे के भीतर प्रदान की जाएगी। आप ईमेल और व्हाट्सएप के माध्यम से अपनी रिपोर्ट प्राप्त करेंगे।
                </p>
              </div>
            </div>
          </div> */}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {loading ? (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500">लैब टेस्ट लोड हो रहे हैं...</p>
              </div>
            ) : filteredTests.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500">कोई लैब टेस्ट नहीं मिला। कृपया एक अलग खोज का प्रयास करें।</p>
              </div>
            ) : filteredTests.map((test) => (
              <div
  key={test._id}
  className="group relative rounded-2xl p-[1px] bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-500 hover:scale-[1.03] transition duration-500"
>

  {/* Card */}
  <div className="bg-white rounded-2xl p-6 h-full shadow-lg group-hover:shadow-2xl transition">

    {/* Top Section */}
    <div className="flex justify-between items-start mb-4">

      <h3 className="text-xl font-semibold text-gray-800 group-hover:text-teal-600 transition">
        {test.name}
      </h3>

      {/* Discount Badge */}
      <span className="bg-red-100 text-red-600 text-xs font-semibold px-2 py-1 rounded-full">
        {Math.round(((test.originalPrice - test.price) / test.originalPrice) * 100)}% OFF
      </span>

    </div>

    {/* Info Section */}
    <div className="space-y-2 text-sm text-gray-600 mb-5">

      {/* Test ID */}
      {test.testId && (
        <div className="flex justify-between items-center pb-2 border-b border-gray-200">
          <span className="flex items-center gap-1 text-xs font-semibold text-gray-700">
            📋 Test ID
          </span>
          <span className="font-medium text-gray-900 text-xs bg-gray-100 px-2 py-1 rounded">
            {test.testId}
          </span>
        </div>
      )}

      <div className="flex justify-between items-center">
        <span className="flex items-center gap-1">
          🧪 Sample
        </span>
        <span className="font-medium text-gray-800">
          {test.sampleType}
        </span>
      </div>

      <div className="flex justify-between items-center">
        <span className="flex items-center gap-1">
          ⏳ Fasting
        </span>

        <span
          className={`text-xs px-2 py-1 rounded-full font-medium ${
            test.fastingRequired
              ? "bg-orange-100 text-orange-600"
              : "bg-green-100 text-green-600"
          }`}
        >
          {test.fastingRequired ? "Required" : "Not Required"}
        </span>
      </div>

    </div>

    {/* Parameters */}
    <div className="mb-5">

      <p className="text-sm font-semibold text-gray-700 mb-2">
        Parameters Included
      </p>

      <div className="flex flex-wrap gap-2">

        {test.parameters.slice(0, 3).map((param, index) => (
          <span
            key={index}
            className="text-xs bg-gradient-to-r from-teal-50 to-cyan-50 text-teal-700 px-3 py-1 rounded-full border"
          >
            {param}
          </span>
        ))}

        {test.parameters.length > 3 && (
          <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
            +{test.parameters.length - 3} more
          </span>
        )}

      </div>

    </div>

    {/* Bottom Section */}
    <div className="flex justify-between items-center">

      {/* Price */}
      <div>
        <span className="text-3xl font-bold text-teal-600">
          ₹{test.price}
        </span>

        <span className="text-sm text-gray-400 line-through ml-2">
          ₹{test.originalPrice}
        </span>
      </div>

      {/* Button */}
      <button
        onClick={() => navigate(`/lab-test/${test._id}`)}
        className="relative bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-md hover:shadow-lg hover:scale-105 transition"
      >
        View Details
      </button>

    </div>

  </div>
</div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-teal-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-4">हमारी लैब सेवाओं को क्यों चुनें?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center text-white">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">सटीक परिणाम</h3>
              <p className="text-teal-100">NABL मान्यता प्राप्त लैब सटीक और विश्वसनीय परीक्षण परिणाम सुनिश्चित करता है</p>
            </div>
            <div className="text-center text-white">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">तेज़ रिपोर्ट</h3>
              <p className="text-teal-100">तेज़ टर्नअराउंड समय के साथ डिजिटल रिपोर्ट ऑनलाइन डिलीवर की जाती है</p>
            </div>
            <div className="text-center text-white">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">होम कलेक्शन</h3>
              <p className="text-teal-100">अपने घर के आराम से मुफ़्त नमूना संग्रहण</p>
            </div>
            <div className="text-center text-white">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">विशेषज्ञ समर्थन</h3>
              <p className="text-teal-100">24/7 ग्राहक सहायता और चिकित्सा परामर्श उपलब्ध है</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">अक्सर पूछे जाने वाले प्रश्न</h2>
            <p className="text-gray-600">हमारी लैब सेवाओं के बारे में सामान्य प्रश्नों के त्वरित उत्तर</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details key={index} className="border border-gray-200 rounded-lg">
                <summary className="p-4 cursor-pointer hover:bg-gray-50 font-medium text-gray-900">
                  {faq.question}
                </summary>
                <div className="p-4 pt-0 text-gray-600">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area Notice */}
      <section className="bg-amber-50 border-t-4 border-amber-500 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-amber-800 font-medium">
                🏘️ यह सेवा वर्तमान में <strong>उदयपुर शहर</strong> में उपलब्ध है। अन्य शहरों में जल्द ही शुरू होगी।
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-teal-50 to-blue-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">अपनी स्वास्थ्य यात्रा शुरू करने के लिए तैयार हैं?</h2>
          <p className="text-xl text-gray-600 mb-8">
            आज ही अपना लैब टेस्ट बुक करें और बेहतर स्वास्थ्य की ओर पहला कदम उठाएं
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* <button className="bg-teal-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors">
              अभी लैब टेस्ट बुक करें
            </button> */}
            {/* <button className="flex items-center justify-center border border-teal-600 text-teal-600 px-8 py-3 rounded-lg font-medium hover:bg-teal-50 transition-colors">
              <Phone className="w-5 h-5 mr-2" />
              +91 XXX XXX XXXX पर कॉल करें
            </button> */}
          </div>
          <p className="text-xs text-gray-600 mt-6 pt-6 border-t border-gray-300">
            No refund policy — only booking and rescheduling available.
          </p>
        </div>
      </section>
    </div>
  );
};

export default LabTestPage;